/**
 * CustomPromptModal Component
 * Elegant modal for custom AI prompts
 * 
 * Features:
 * - Clean, minimalist design
 * - Auto-focus on textarea
 * - Enter to submit (Shift+Enter for newline)
 * - Escape to close
 * - Click outside to close
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Loader2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getWorkspaceColors } from '../styles';

const CustomPromptModal = ({
  isOpen,
  onClose,
  onSubmit,
  isProcessing = false,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [prompt, setPrompt] = useState('');
  const textareaRef = useRef(null);
  const modalRef = useRef(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset prompt when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || isProcessing) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isProcessing]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (isProcessing) return;
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose?.();
    }
  };

  // Handle submit
  const handleSubmit = () => {
    if (!prompt.trim() || isProcessing) return;
    onSubmit?.(prompt.trim());
  };

  // Handle key press in textarea
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  const styles = {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 20,
    },
    modal: {
      backgroundColor: workspaceColors.bgPrimary,
      borderRadius: 16,
      width: '100%',
      maxWidth: 520,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      overflow: 'hidden',
      animation: 'modalSlideIn 0.2s ease-out',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    headerIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: `${workspaceColors.accentPrimary}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: workspaceColors.accentPrimary,
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: workspaceColors.textPrimary,
      margin: 0,
    },
    headerSubtitle: {
      fontSize: 12,
      color: workspaceColors.textSecondary,
      margin: 0,
      marginTop: 2,
    },
    closeBtn: {
      width: 36,
      height: 36,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textTertiary,
      cursor: isProcessing ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      transition: 'all 0.15s ease',
      opacity: isProcessing ? 0.5 : 1,
    },
    body: {
      padding: 20,
    },
    textareaWrapper: {
      backgroundColor: workspaceColors.bgSecondary,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      overflow: 'hidden',
      transition: 'border-color 0.15s ease',
    },
    textarea: {
      width: '100%',
      minHeight: 120,
      maxHeight: 240,
      padding: 16,
      border: 'none',
      background: 'transparent',
      color: workspaceColors.textPrimary,
      fontSize: 14,
      lineHeight: 1.6,
      resize: 'vertical',
      outline: 'none',
      fontFamily: 'inherit',
    },
    hint: {
      fontSize: 11,
      color: workspaceColors.textTertiary,
      marginTop: 10,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 12,
      padding: '16px 20px',
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
    },
    cancelBtn: {
      padding: '10px 20px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: 'transparent',
      color: workspaceColors.textSecondary,
      fontSize: 14,
      fontWeight: 500,
      cursor: isProcessing ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease',
      opacity: isProcessing ? 0.5 : 1,
    },
    submitBtn: {
      padding: '10px 24px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: prompt.trim() && !isProcessing 
        ? workspaceColors.accentPrimary 
        : workspaceColors.bgSecondary,
      color: prompt.trim() && !isProcessing 
        ? 'white' 
        : workspaceColors.textTertiary,
      fontSize: 14,
      fontWeight: 600,
      cursor: prompt.trim() && !isProcessing ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.15s ease',
    },
    spinner: {
      animation: 'spin 1s linear infinite',
    },
  };

  const modalContent = (
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <Sparkles size={20} />
            </div>
            <div>
              <h3 style={styles.headerTitle}>Prompt Personalizado</h3>
              <p style={styles.headerSubtitle}>Escreva sua solicitação para a IA</p>
            </div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) {
                e.currentTarget.style.backgroundColor = workspaceColors.bgTertiary;
                e.currentTarget.style.color = workspaceColors.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = workspaceColors.textTertiary;
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div 
            style={styles.textareaWrapper}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = workspaceColors.accentPrimary;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = workspaceColors.borderColor;
            }}
          >
            <textarea
              ref={textareaRef}
              style={styles.textarea}
              placeholder="Ex: Resuma o conteúdo do PDF anexado, Crie uma proposta comercial, Analise os dados..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isProcessing}
            />
          </div>
          <div style={styles.hint}>
            <span>💡</span>
            <span>Pressione Enter para enviar ou Shift+Enter para nova linha</span>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            style={styles.cancelBtn}
            onClick={onClose}
            disabled={isProcessing}
            onMouseEnter={(e) => {
              if (!isProcessing) {
                e.currentTarget.style.backgroundColor = workspaceColors.bgSecondary;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancelar
          </button>
          <button
            style={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!prompt.trim() || isProcessing}
            onMouseEnter={(e) => {
              if (prompt.trim() && !isProcessing) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {isProcessing ? (
              <>
                <Loader2 size={18} style={styles.spinner} />
                Gerando...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Gerar
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CustomPromptModal;